import * as ft from 'funtypes'

/**
 * Validation helpers shared by every trust boundary in the repo.
 *
 * House rules:
 * - a type is declared exactly once, as a funtypes runtype; the TypeScript type
 *   is derived with `Static<typeof X>`. Never hand-write an interface next to a
 *   validator.
 * - at a trust boundary (network response, file read, fetched artifact) prefer
 *   {@link validate} over `.parse()`, so the caller can report a useful error
 *   instead of catching a throw.
 */

/** Extra context folded into a failure message so an error names its row. */
export interface ValidationContext {
  /** What was being parsed, e.g. `pool.json` or `stations.geojson`. */
  source?: string
  /** Identifier of the offending row, when parsing row by row. */
  rowId?: string | number
}

export type Validated<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: string; readonly failure: ft.Failure }

/** Error carrying the original funtypes failure, thrown by {@link parseOrThrow}. */
export class ValidationError extends Error {
  readonly failure: ft.Failure
  readonly context: ValidationContext

  constructor(message: string, failure: ft.Failure, context: ValidationContext) {
    super(message)
    this.name = 'ValidationError'
    this.failure = failure
    this.context = context
  }
}

/**
 * Turn a funtypes failure into a one-line message naming the offending field
 * path and, when known, the source file and row id.
 *
 * `pool.json row 4211: latitude: Expected number, but was string`
 */
export const describeFailure = (failure: ft.Failure, context: ValidationContext = {}): string => {
  const where: string[] = []
  if (context.source !== undefined) where.push(context.source)
  if (context.rowId !== undefined) where.push(`row ${context.rowId}`)

  const path = failure.key === undefined || failure.key === '' ? '<root>' : failure.key
  const prefix = where.length > 0 ? `${where.join(' ')}: ` : ''
  return `${prefix}${path}: ${failure.message}`
}

/** Parse without throwing. The failure branch carries a ready-to-log message. */
export const validate = <T>(
  runtype: ft.RuntypeBase<T>,
  value: unknown,
  context: ValidationContext = {},
): Validated<T> => {
  const result = runtype.safeParse(value)
  if (result.success) return { ok: true, value: result.value }
  return { ok: false, error: describeFailure(result, context), failure: result }
}

/** Parse, throwing a {@link ValidationError} whose message names the field path. */
export const parseOrThrow = <T>(
  runtype: ft.RuntypeBase<T>,
  value: unknown,
  context: ValidationContext = {},
): T => {
  const result = validate(runtype, value, context)
  if (result.ok) return result.value
  throw new ValidationError(result.error, result.failure, context)
}

export interface ValidateRowsOptions {
  /** What is being parsed, e.g. `pool.json`. */
  source?: string
  /** Pull the row's own id out of the raw value. Defaults to the array index. */
  rowId?: (row: unknown, index: number) => string | number
}

/**
 * Parse an array row by row so a failure can name the row's own id rather than
 * its array index. Returns every valid row plus every error, because a single
 * malformed row from an upstream registry should not discard the other 40 000.
 */
export const validateRows = <T>(
  runtype: ft.RuntypeBase<T>,
  rows: readonly unknown[],
  options: ValidateRowsOptions = {},
): { readonly rows: T[]; readonly errors: string[] } => {
  const valid: T[] = []
  const errors: string[] = []

  rows.forEach((row, index) => {
    const rowId = options.rowId?.(row, index) ?? index
    const result = validate(runtype, row, { source: options.source, rowId })
    if (result.ok) valid.push(result.value)
    else errors.push(result.error)
  })

  return { rows: valid, errors }
}

/**
 * An object runtype with a required half and an optional half.
 *
 * funtypes has no per-field `Optional`, so optionality is expressed by
 * intersecting `Object` with `Partial`. This wrapper keeps that idiom in one
 * place instead of spelling it out at every definition.
 */
export const objectWith = <R extends ft.RecordFields, O extends ft.RecordFields>(
  required: R,
  optional: O,
) => ft.Intersect(ft.Object(required), ft.Partial(optional))

/** `T | null`, for upstream fields that are present but explicitly empty. */
export const nullable = <T extends ft.RuntypeBase>(runtype: T) => ft.Union(runtype, ft.Null)

/** A non-empty string. */
export const NonEmptyString = ft.String.withConstraint(
  (value) => value.length > 0 || 'Expected a non-empty string',
  { name: 'NonEmptyString' },
)

/**
 * An ISO-8601 instant, e.g. `2026-08-15T09:30:00+02:00`.
 * Deliberately permissive about the offset: EIPA emits local Warsaw offsets and
 * Open Charge Map emits `Z`.
 */
export const IsoDateTime = ft.String.withConstraint(
  (value) =>
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.test(value) ||
    `Expected an ISO-8601 date-time, but was ${JSON.stringify(value)}`,
  { name: 'IsoDateTime' },
)

/** A WGS84 latitude. */
export const Latitude = ft.Number.withConstraint(
  (value) => (value >= -90 && value <= 90) || `Expected a latitude in [-90, 90], but was ${value}`,
  { name: 'Latitude' },
)

/** A WGS84 longitude. */
export const Longitude = ft.Number.withConstraint(
  (value) =>
    (value >= -180 && value <= 180) || `Expected a longitude in [-180, 180], but was ${value}`,
  { name: 'Longitude' },
)
